import * as React from 'react';

import Retracked from 'js/app/retracked';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import { ALL_PRODUCTS_INDEX_TYPE, INDEX_TYPE_TO_NAME_EXPERIMENT_PARAMETER_NAME } from 'bundles/search-common/constants';
import useUrlData from 'bundles/shared-articles/hooks/useArticleUrlData';
import { getPageNumberFromString, isValidPageNumber } from 'bundles/shared-articles/utils';
import PageDataErrorHandler from 'bundles/unified-seo-content-common/components/shared/PageDataErrorHandler';
import HubPageSkeleton from 'bundles/unified-seo-content-common/components/skeletons/HubPageSkeleton';
import { ITEMS_PER_CATEGORY_PAGE, SEO_CONTENT_PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import useDiscoveryCollectionsRecs from 'bundles/unified-seo-content-common/hooks/useDiscoveryCollectionsRecs';
import useSeoContentCategoryPageQuery from 'bundles/unified-seo-content-common/hooks/useSeoContentCategoryPageQuery';
import useSeoContentFilterQuery from 'bundles/unified-seo-content-common/hooks/useSeoContentFilterQuery';
import useSeoContentHubPageLink from 'bundles/unified-seo-content-common/hooks/useSeoContentHubPageLink';
import ResourceCategoryPage from 'bundles/unified-seo-content-common/pages/resource/ResourceCategoryPage';
import PageInfoProvider from 'bundles/unified-seo-content-common/providers/PageInfoProvider';

const ResourceCategoryPageRoot = () => {
  const pageType = SEO_CONTENT_PAGE_TYPE.resources;

  const { page, slug, isPreview, subfolderLocaleCode, pathname } = useUrlData();

  const currentPageNumber = getPageNumberFromString(page);

  const skipCount = (currentPageNumber - 1) * ITEMS_PER_CATEGORY_PAGE;
  const indexTypeToNameMap = () => GrowthDiscoveryExperiments.get(INDEX_TYPE_TO_NAME_EXPERIMENT_PARAMETER_NAME);
  const productRecsIndexName = indexTypeToNameMap()[ALL_PRODUCTS_INDEX_TYPE];

  const {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentLinks,
    bottomContentJson,
    discoveryCollectionSetId,
    isHubPageDataLoading,
    productRecommendationTitle,
    productRecommendationsSearchUrl,
    navigationLinks,
    contentFilters,
  } = useSeoContentCategoryPageQuery({
    slug,
    pageType,
    preview: isPreview,
    subfolderLocaleCode,
  });

  const { contentList, totalNumberOfContent, isSeoContentDataLoading } = useSeoContentFilterQuery({
    filters: contentFilters,
    skipCount,
    preview: isPreview,
  });

  const hubPageLink = useSeoContentHubPageLink({
    pageType: SEO_CONTENT_PAGE_TYPE.resources,
    preview: isPreview,
    subfolderLocaleCode,
  });

  const { recommendationCollections, loadingRecommendations } = useDiscoveryCollectionsRecs(
    discoveryCollectionSetId || ''
  );
  const pageCount = totalNumberOfContent ? Math.ceil(totalNumberOfContent / ITEMS_PER_CATEGORY_PAGE) : 0;

  const isCategoryPageDataLoading = isHubPageDataLoading || isSeoContentDataLoading;

  if (isCategoryPageDataLoading) {
    return <HubPageSkeleton />;
  }

  const notFoundError = !title || !contentList;

  if (!isValidPageNumber(currentPageNumber, pageCount) || notFoundError) {
    return <PageDataErrorHandler />;
  }

  const pageData = {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentLinks,
    bottomContentJson,
    discoveryCollectionSetId,
    productRecommendationTitle,
    productRecommendationsSearchUrl,
    navigationLinks,
    contentList,
    totalNumberOfContent,
    recommendationCollections,
    loadingRecommendations,
    productRecsIndexName,
    pageCount,
    hubPageLink,
  };

  return (
    <PageInfoProvider pageType={pageType}>
      <ResourceCategoryPage
        pageData={pageData}
        slug={slug}
        currentPageNumber={currentPageNumber}
        pageCount={pageCount}
        pathname={pathname}
      />
    </PageInfoProvider>
  );
};

export default Retracked.createTrackedContainer(() => ({
  namespace: {
    app: 'resources',
    page: 'resource_category_page',
  },
}))(ResourceCategoryPageRoot);
